import {
  Canvas, StaticCanvas, Textbox, Rect, Circle, Triangle, Polygon, Group, FabricImage, Gradient, Path, Ellipse, FabricObject, Point,
  filters, loadSVGFromString, util,
} from 'fabric';
import type { IconNode } from 'lucide';
import { assetObjectUrl } from '../../features/studio-controller/StudioServices'

// ---------------------------------------------------------------- types

export interface Frame {
  x: number;
  y: number;
  w: number;
  h: number;
  radius?: number;
  shape?: FrameShape;
}

export interface Look {
  preset: string;
  intensity: number;
  adj: Record<string, number>;
  tint: { color: string; amount: number } | null;
}

export type TextEffectType = 'none' | 'shadow' | 'lift' | 'hollow' | 'splice' | 'outline' | 'neon' | 'glitch' | 'background';

export interface TextEffect {
  type: TextEffectType;
  color?: string;
  amount?: number;
  baseFill?: string;
}

export interface IconInfo { name: string; color: string; strokeWidth: number }
export interface QrInfo { text: string; dark: string; light: string }

/** Custom properties that Studio stores on Fabric objects (also saved/loaded via EXTRA_PROPS). */
declare module 'fabric' {
  interface FabricObject {
    id?: string;
    name?: string;
    assetUrl?: string;
    placeholder?: string;
    frame?: Frame;
    locked?: boolean;
    isBackgroundImage?: boolean;
    look?: Look;
    effect?: TextEffect | null;
    qr?: QrInfo;
    icon?: IconInfo;
    galleryId?: string | number;
    credit?: string;
    shapeKind?: string;
    /** Charts and tables keep their data (elementKit ChartSpec / TableSpec) so they can be edited. */
    chart?: unknown;
    table?: unknown;
  }
  interface ObjectProps {
    id?: string;
    name?: string;
    assetUrl?: string;
    placeholder?: string;
    frame?: Frame;
    locked?: boolean;
    isBackgroundImage?: boolean;
    look?: Look;
    effect?: TextEffect | null;
    qr?: QrInfo;
    icon?: IconInfo;
    galleryId?: string | number;
    credit?: string;
    shapeKind?: string;
    /** Charts and tables keep their data (elementKit ChartSpec / TableSpec) so they can be edited. */
    chart?: unknown;
    table?: unknown;
  }
  interface SerializedObjectProps {
    id?: string;
    name?: string;
    assetUrl?: string;
    placeholder?: string;
    frame?: Frame;
    locked?: boolean;
    isBackgroundImage?: boolean;
    look?: Look;
    effect?: TextEffect | null;
    qr?: QrInfo;
    icon?: IconInfo;
    galleryId?: string | number;
    credit?: string;
    shapeKind?: string;
    /** Charts and tables keep their data (elementKit ChartSpec / TableSpec) so they can be edited. */
    chart?: unknown;
    table?: unknown;
  }
}

/** Either kind of Fabric canvas (interactive or off-screen). */
export type AnyCanvas = Canvas | StaticCanvas;
type FilterList = NonNullable<FabricImage['filters']>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;
interface Box { left: number; top: number; width: number; height: number }

/** Extra properties saved with every object (Fabric drops unknown ones otherwise). */
export const EXTRA_PROPS = ['id', 'name', 'assetUrl', 'placeholder', 'frame', 'locked', 'isBackgroundImage', 'selectable', 'evented',
  'lockMovementX', 'lockMovementY', 'lockScalingX', 'lockScalingY', 'lockRotation', 'hasControls', 'look', 'effect', 'qr', 'icon',
  'galleryId', 'credit', 'visible', 'shapeKind', 'chart', 'table'];

// Canva-like handles: round white corners with a green border.
FabricObject.ownDefaults.cornerStyle = 'circle';
FabricObject.ownDefaults.cornerColor = '#ffffff';
FabricObject.ownDefaults.cornerStrokeColor = '#0b7a5a';
FabricObject.ownDefaults.borderColor = '#0b7a5a';
FabricObject.ownDefaults.transparentCorners = false;
FabricObject.ownDefaults.cornerSize = 12;
FabricObject.ownDefaults.borderScaleFactor = 1.5;

let seq = 0;
export const newId = (): string => `o${Date.now().toString(36)}${(seq++).toString(36)}`;

// ---------------------------------------------------------------- gradients

/** A linear gradient across a w×h box at a CSS-like angle (0 = bottom to top, 90 = left to right). */
export function linearGradient(colors: string[], angle: number, w: number, h: number): Gradient<'linear'> {
  const rad = ((angle - 90) * Math.PI) / 180;
  const half = (Math.abs(w * Math.cos(rad)) + Math.abs(h * Math.sin(rad))) / 2;
  const cx = w / 2;
  const cy = h / 2;
  return new Gradient({
    type: 'linear',
    gradientUnits: 'pixels',
    coords: { x1: cx - Math.cos(rad) * half, y1: cy - Math.sin(rad) * half, x2: cx + Math.cos(rad) * half, y2: cy + Math.sin(rad) * half },
    colorStops: colors.map((c, i) => ({ offset: colors.length === 1 ? 0 : i / (colors.length - 1), color: c })),
  });
}

// ---------------------------------------------------------------- template layouts → objects

interface LayerBase { opacity?: number; stroke?: string; strokeWidth?: number }
export type TemplateLayer =
  | (LayerBase & { type: 'rect'; x: number; y: number; w: number; h: number; fill: string; radius?: number })
  | (LayerBase & { type: 'circle'; cx: number; cy: number; r: number; fill: string })
  | (LayerBase & {
    type: 'text'; text: string; x: number; y: number; w: number; fontSize: number; fontFamily?: string; fontWeight?: number | string;
    fontStyle?: string; fill?: string; align?: string; lineHeight?: number; charSpacing?: number;
  })
  | (LayerBase & { type: 'image'; x: number; y: number; w: number; h: number; radius?: number; placeholder?: string });

export interface TemplateSpec {
  background?: { type: 'color'; color?: string } | { type: 'gradient'; colors: string[]; angle?: number };
  layers?: TemplateLayer[];
}

/** Builds the objects of a template layout (see DesignTemplateSeeder for the format). */
export function buildFromSpec(canvas: AnyCanvas, spec: TemplateSpec, width: number, height: number): void {
  const bg = spec.background || { type: 'color', color: '#ffffff' };
  canvas.backgroundColor = bg.type === 'gradient' ? linearGradient(bg.colors, bg.angle ?? 135, width, height) : bg.color || '#ffffff';
  for (const l of spec.layers || []) {
    const o = layerToObject(l);
    if (o) canvas.add(o);
  }
  canvas.requestRenderAll();
}

function layerToObject(l: TemplateLayer): FabricObject | null {
  const common = { id: newId(), opacity: l.opacity ?? 1, stroke: l.stroke || null, strokeWidth: l.strokeWidth || 0 };
  switch (l.type) {
    case 'rect':
      return new Rect({ ...common, name: 'Rectangle', left: l.x, top: l.y, width: l.w, height: l.h, fill: l.fill, rx: l.radius || 0, ry: l.radius || 0 });
    case 'circle':
      return new Circle({ ...common, name: 'Circle', left: l.cx - l.r, top: l.cy - l.r, radius: l.r, fill: l.fill });
    case 'text':
      return new Textbox(l.text, {
        ...common, name: 'Text', left: l.x, top: l.y, width: l.w, fontSize: l.fontSize, fontFamily: l.fontFamily || 'Poppins',
        fontWeight: l.fontWeight || 400, fontStyle: (l.fontStyle || 'normal') as 'normal', fill: l.fill || '#111', textAlign: l.align || 'left',
        lineHeight: l.lineHeight || 1.16, charSpacing: l.charSpacing || 0, splitByGrapheme: false,
      });
    case 'image':
      return makePlaceholder({ x: l.x, y: l.y, w: l.w, h: l.h, radius: l.radius || 0 }, l.placeholder || 'Your photo');
    default:
      return null;
  }
}

// ---------------------------------------------------------------- frame shapes

/** Shapes a photo frame can have. Path shapes are drawn in a 100×100 box. */
export const FRAME_SHAPES = {
  rect: null,
  circle: null,
  arch: 'M0 100 L0 50 A50 50 0 0 1 100 50 L100 100 Z',
  heart: 'M50 100 C20 78 0 58 0 32 C0 12 14 0 30 0 C40 0 47 5 50 13 C53 5 60 0 70 0 C86 0 100 12 100 32 C100 58 80 78 50 100 Z',
  hexagon: 'M25 0 L75 0 L100 50 L75 100 L25 100 L0 50 Z',
  star: starPath(50, 22, 5),
  blob: 'M52 0 C78 2 100 20 98 48 C96 76 76 100 46 98 C18 96 0 76 2 48 C4 20 26 -2 52 0 Z',
  diamond: 'M50 0 L100 50 L50 100 L0 50 Z',
} as const;

export type FrameShape = keyof typeof FRAME_SHAPES;

function starPath(outer: number, inner: number, n: number): string {
  const pts: string[] = [];
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI / n) * i - Math.PI / 2;
    pts.push(`${(50 + r * Math.cos(a)).toFixed(2)} ${(50 + r * Math.sin(a)).toFixed(2)}`);
  }
  return `M${pts.join(' L')} Z`;
}

/** A clip shape of w×h (object coordinates, centred). radius is in the same units. */
function clipShape(shape: FrameShape | undefined, w: number, h: number, radius: number): FabricObject | undefined {
  if (shape === 'circle') return new Ellipse({ rx: w / 2, ry: h / 2, originX: 'center', originY: 'center' });
  const d = shape ? FRAME_SHAPES[shape] : null;
  if (d) {
    const p = new Path(d, { originX: 'center', originY: 'center' });
    p.set({ scaleX: w / p.width, scaleY: h / p.height, left: 0, top: 0 });
    return p;
  }
  return radius ? new Rect({ width: w, height: h, rx: radius, ry: radius, originX: 'center', originY: 'center' }) : undefined;
}

/** A grey box that says "Add photo". Choosing an image while it is selected fills it. */
export function makePlaceholder(frame: Frame, label: string): Group {
  const shape: FrameShape = frame.shape || 'rect';
  const stroke = { fill: '#dfe7e3', stroke: '#9fb3ab', strokeWidth: Math.max(2, frame.w / 200), strokeDashArray: [frame.w / 40, frame.w / 60], strokeUniform: true };
  let box: FabricObject;
  if (shape === 'rect') {
    box = new Rect({ left: 0, top: 0, width: frame.w, height: frame.h, rx: frame.radius ?? 0, ry: frame.radius ?? 0, ...stroke });
  } else if (shape === 'circle') {
    box = new Ellipse({ left: 0, top: 0, rx: frame.w / 2, ry: frame.h / 2, ...stroke });
  } else {
    const p = new Path(FRAME_SHAPES[shape] as string, { left: 0, top: 0, ...stroke });
    p.set({ scaleX: frame.w / p.width, scaleY: frame.h / p.height });
    box = p;
  }
  const fs = Math.max(16, Math.min(frame.w, frame.h) / 11);
  const text = new Textbox(`＋\n${label}`, {
    left: 0, top: frame.h / 2 - fs * 1.2, width: frame.w, fontSize: fs, fontFamily: 'Poppins', fontWeight: 600,
    fill: '#4f625b', textAlign: 'center', lineHeight: 1.1,
  });
  return new Group([box, text], {
    id: newId(), name: label, left: frame.x, top: frame.y, placeholder: label, frame: { ...frame, shape }, subTargetCheck: false,
  });
}

/** Photo grids: frames laid out inside the page, with a gap. */
export type GridCell = [number, number, number, number];

export const GRIDS: Record<string, GridCell[]> = {
  two: [[0, 0, 0.5, 1], [0.5, 0, 0.5, 1]],
  twoRows: [[0, 0, 1, 0.5], [0, 0.5, 1, 0.5]],
  three: [[0, 0, 0.6, 1], [0.6, 0, 0.4, 0.5], [0.6, 0.5, 0.4, 0.5]],
  threeCols: [[0, 0, 1 / 3, 1], [1 / 3, 0, 1 / 3, 1], [2 / 3, 0, 1 / 3, 1]],
  four: [[0, 0, 0.5, 0.5], [0.5, 0, 0.5, 0.5], [0, 0.5, 0.5, 0.5], [0.5, 0.5, 0.5, 0.5]],
  six: [[0, 0, 1 / 3, 0.5], [1 / 3, 0, 1 / 3, 0.5], [2 / 3, 0, 1 / 3, 0.5], [0, 0.5, 1 / 3, 0.5], [1 / 3, 0.5, 1 / 3, 0.5], [2 / 3, 0.5, 1 / 3, 0.5]],
}

export type GridKind = keyof typeof GRIDS;

export function makeGrid(kind: GridKind, width: number, height: number, gapRatio = 0.012): Group[] {
  const gap = Math.round(Math.min(width, height) * gapRatio);
  return (GRIDS[kind] as number[][]).map(([x, y, w, h], i) => makePlaceholder({
    x: x * width + gap, y: y * height + gap, w: w * width - gap * 2, h: h * height - gap * 2, radius: 0, shape: 'rect',
  }, `Photo ${i + 1}`));
}

// ---------------------------------------------------------------- images

/** A stored image (S3 URL) as a Fabric image, loaded through the same-origin proxy. */
export async function loadImage(assetUrl: string, extra: Partial<FabricImage> & Record<string, unknown> = {}): Promise<FabricImage> {
  const src = await assetObjectUrl(assetUrl);
  const img = await FabricImage.fromURL(src);
  img.set({ id: newId(), name: 'Image', assetUrl, ...extra });
  return img;
}

const natural = (img: FabricImage): { w: number; h: number } => {
  const el = img.getOriginalSize ? img.getOriginalSize() : null;
  const dom = img.getElement() as HTMLImageElement;
  return { w: el?.width || dom.naturalWidth || img.width, h: el?.height || dom.naturalHeight || img.height };
};

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/** Crops and scales an image to cover a frame exactly, clipped to the frame's shape. */
export function fitImageToFrame(img: FabricImage, frame: Frame): FabricImage {
  const { w: iw, h: ih } = natural(img);
  const scale = Math.max(frame.w / iw, frame.h / ih);
  const cw = frame.w / scale;
  const ch = frame.h / scale;
  img.set({
    cropX: (iw - cw) / 2, cropY: (ih - ch) / 2, width: cw, height: ch, scaleX: scale, scaleY: scale,
    left: frame.x, top: frame.y, angle: 0, frame,
  });
  img.clipPath = clipShape(frame.shape, cw, ch, (frame.radius || 0) / scale);
  img.setCoords();
  return img;
}

/** Fits an image inside the design (at most 70% of it), centred. */
export function placeImage(img: FabricImage, width: number, height: number): FabricImage {
  const scale = Math.min((width * 0.7) / img.width, (height * 0.7) / img.height, 1);
  img.set({ scaleX: scale, scaleY: scale, left: (width - img.width * scale) / 2, top: (height - img.height * scale) / 2 });
  img.setCoords();
  return img;
}

/** Corner rounding (in design pixels) of an image without a shaped frame. */
export const imageRadius = (img: FabricImage): number => Math.round(((img.clipPath as Rect | undefined)?.rx || 0) * img.scaleX);

export function setImageRadius(img: FabricImage, r: number): void {
  img.clipPath = r ? new Rect({ width: img.width, height: img.height, rx: r / img.scaleX, ry: r / img.scaleY, originX: 'center', originY: 'center' }) : undefined;
  if (img.frame) img.frame = { ...img.frame, radius: r, shape: 'rect' };
  img.dirty = true;
}

/** Re-cuts the clip after the crop window changed. */
function refreshClip(img: FabricImage, radius: number): void {
  if (img.frame && img.frame.shape && img.frame.shape !== 'rect') {
    img.clipPath = clipShape(img.frame.shape, img.width, img.height, 0);
  } else {
    setImageRadius(img, radius);
  }
  img.dirty = true;
}

export interface CropState { zoom: number; iw: number; ih: number; maxW: number; maxH: number }

/**
 * Crop state of an image: the visible box stays where it is; zoom (1 = the whole image fits the box)
 * and the window position change. Used by crop mode.
 */
export function readCrop(img: FabricImage): CropState {
  const { w: iw, h: ih } = natural(img);
  const boxRatio = img.width / img.height;
  const maxW = Math.min(iw, ih * boxRatio);
  return { zoom: maxW / img.width, iw, ih, maxW, maxH: maxW / boxRatio };
}

export function setCropZoom(img: FabricImage, zoom: number): void {
  const radius = imageRadius(img);
  const { iw, ih, maxW, maxH } = readCrop(img);
  const boxW = img.getScaledWidth();
  const boxH = img.getScaledHeight();
  const cx = img.cropX + img.width / 2;
  const cy = img.cropY + img.height / 2;
  const cw = maxW / zoom;
  const ch = maxH / zoom;
  img.set({
    width: cw, height: ch, scaleX: boxW / cw, scaleY: boxH / ch,
    cropX: clamp(cx - cw / 2, 0, iw - cw), cropY: clamp(cy - ch / 2, 0, ih - ch),
  });
  refreshClip(img, radius);
}

/** Moves the crop window by a drag on the design (dx, dy in design pixels). */
export function panCrop(img: FabricImage, start: { x: number; y: number }, dx: number, dy: number): void {
  const { iw, ih } = readCrop(img);
  const a = (-(img.angle || 0) * Math.PI) / 180;
  const lx = (dx * Math.cos(a) - dy * Math.sin(a)) * (img.flipX ? -1 : 1);
  const ly = (dx * Math.sin(a) + dy * Math.cos(a)) * (img.flipY ? -1 : 1);
  img.set({
    cropX: clamp(start.x - lx / img.scaleX, 0, iw - img.width),
    cropY: clamp(start.y - ly / img.scaleY, 0, ih - img.height),
  });
  img.dirty = true;
}

/** Changes the visible box to an aspect ratio (null = the photo's own), keeping its area and centre. */
export function setCropAspect(img: FabricImage, ratio: number | null): void {
  const radius = imageRadius(img);
  const { w: iw, h: ih } = natural(img);
  const r = ratio || iw / ih;
  const center = img.getCenterPoint();
  const area = img.getScaledWidth() * img.getScaledHeight();
  const boxW = Math.sqrt(area * r);
  const boxH = boxW / r;
  const cw = Math.min(iw, ih * r);
  const ch = cw / r;
  img.set({ width: cw, height: ch, cropX: (iw - cw) / 2, cropY: (ih - ch) / 2, scaleX: boxW / cw, scaleY: boxH / ch });
  img.setPositionByOrigin(center, 'center', 'center');
  if (img.frame) img.frame = { ...img.frame, w: boxW, h: boxH };
  refreshClip(img, radius);
  img.setCoords();
}

/** Swaps the picture inside an image (e.g. the background-removed cut-out), keeping size, crop and position. */
export async function swapImageSource(img: FabricImage, assetUrl: string): Promise<void> {
  const before = natural(img);
  const radius = imageRadius(img);
  const src = await assetObjectUrl(assetUrl);
  const keep = { cropX: img.cropX, cropY: img.cropY, width: img.width, height: img.height, scaleX: img.scaleX, scaleY: img.scaleY };
  await img.setSrc(src);
  const after = natural(img);
  const k = after.w / before.w;
  img.set({
    assetUrl, cropX: keep.cropX * k, cropY: keep.cropY * k, width: keep.width * k, height: keep.height * k,
    scaleX: keep.scaleX / k, scaleY: keep.scaleY / k,
  });
  refreshClip(img, radius);
  if (img.look) applyLook(img, img.look);
  img.setCoords();
}

// ---------------------------------------------------------------- photo looks (filters) and adjustments

type Matrix = number[];

const ID: Matrix = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0];

/** a after b (4×5 colour matrices). */
function mul(a: Matrix, b: Matrix): Matrix {
  const out: Matrix = new Array(20).fill(0);
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 5; c++) {
      let v = c === 4 ? a[r * 5 + 4] : 0;
      for (let k = 0; k < 4; k++) v += a[r * 5 + k] * b[k * 5 + c];
      out[r * 5 + c] = v;
    }
  }
  return out;
}
const sat = (s: number): Matrix => {
  const lr = 0.2126 * (1 - s); const lg = 0.7152 * (1 - s); const lb = 0.0722 * (1 - s);
  return [lr + s, lg, lb, 0, 0, lr, lg + s, lb, 0, 0, lr, lg, lb + s, 0, 0, 0, 0, 0, 1, 0];
};
const con = (c: number): Matrix => { const o = (1 - c) / 2; return [c, 0, 0, 0, o, 0, c, 0, 0, o, 0, 0, c, 0, o, 0, 0, 0, 1, 0]; };
const tint = (r: number, g: number, b: number, off: [number, number, number] = [0, 0, 0]): Matrix =>
  [r, 0, 0, 0, off[0], 0, g, 0, 0, off[1], 0, 0, b, 0, off[2], 0, 0, 0, 1, 0];
const fabricMatrix = (name: 'Vintage' | 'Kodachrome' | 'Technicolor' | 'Polaroid' | 'Brownie' | 'Sepia' | 'BlackWhite'): Matrix =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [...(new (filters as any)[name]().matrix as number[])];

export interface LookPreset { key: string; label: string; matrix?: () => Matrix }

/** Ready-made looks. Each is a colour matrix, applied at 0–100% strength. */
export const LOOKS: LookPreset[] = [
  { key: 'none', label: 'Original' },
  { key: 'vivid', label: 'Vivid', matrix: () => mul(sat(1.45), con(1.1)) },
  { key: 'warm', label: 'Warm', matrix: () => tint(1.08, 1.0, 0.86, [0.03, 0.01, -0.02]) },
  { key: 'cool', label: 'Cool', matrix: () => tint(0.9, 1.0, 1.1, [-0.01, 0.01, 0.04]) },
  { key: 'golden', label: 'Golden hour', matrix: () => mul(tint(1.1, 1.0, 0.8, [0.05, 0.02, -0.03]), sat(1.15)) },
  { key: 'fade', label: 'Faded', matrix: () => mul(tint(0.85, 0.85, 0.85, [0.09, 0.09, 0.1]), sat(0.8)) },
  { key: 'drama', label: 'Drama', matrix: () => mul(con(1.35), sat(0.7)) },
  { key: 'vintage', label: 'Vintage', matrix: () => fabricMatrix('Vintage') },
  { key: 'kodachrome', label: 'Retro', matrix: () => fabricMatrix('Kodachrome') },
  { key: 'technicolor', label: 'Cinema', matrix: () => fabricMatrix('Technicolor') },
  { key: 'polaroid', label: 'Instant', matrix: () => fabricMatrix('Polaroid') },
  { key: 'brownie', label: 'Brownie', matrix: () => fabricMatrix('Brownie') },
  { key: 'sepia', label: 'Sepia', matrix: () => fabricMatrix('Sepia') },
  { key: 'mono', label: 'Mono', matrix: () => sat(0) },
  { key: 'noir', label: 'Noir', matrix: () => mul(con(1.45), sat(0)) },
  { key: 'bw', label: 'Black & white', matrix: () => fabricMatrix('BlackWhite') },
];

export interface Adjustment { key: string; label: string; min: number; max: number }

export const ADJUSTMENTS: Adjustment[] = [
  { key: 'brightness', label: 'Brightness', min: -100, max: 100 },
  { key: 'contrast', label: 'Contrast', min: -100, max: 100 },
  { key: 'saturation', label: 'Saturation', min: -100, max: 100 },
  { key: 'vibrance', label: 'Vibrance', min: -100, max: 100 },
  { key: 'hue', label: 'Hue', min: -180, max: 180 },
  { key: 'gamma', label: 'Shadows', min: -100, max: 100 },
  { key: 'sharpen', label: 'Sharpen', min: 0, max: 100 },
  { key: 'blur', label: 'Blur', min: 0, max: 100 },
  { key: 'noise', label: 'Grain', min: 0, max: 100 },
  { key: 'pixelate', label: 'Pixelate', min: 0, max: 100 },
];

export const emptyLook = (): Look => ({ preset: 'none', intensity: 100, adj: {}, tint: null });

/** The look of an image (also understands images edited with the first Studio version). */
export function readLook(img: FabricImage): Look {
  if (img.look) return { ...emptyLook(), ...img.look, adj: { ...(img.look.adj || {}) } };
  const look = emptyLook();
  for (const f of (img.filters || []) as Json[]) {
    const t = f.type || f.constructor?.type;
    if (t === 'Brightness') look.adj.brightness = Math.round(f.brightness * 100);
    else if (t === 'Contrast') look.adj.contrast = Math.round(f.contrast * 100);
    else if (t === 'Saturation') look.adj.saturation = Math.round(f.saturation * 100);
    else if (t === 'Blur') look.adj.blur = Math.round(f.blur * 100);
    else if (t === 'Grayscale') look.preset = 'mono';
  }
  return look;
}

/** Builds the Fabric filters for a look. */
export function lookFilters(look: Look): FilterList {
  const list: FilterList = [];
  const preset = LOOKS.find((l) => l.key === look.preset);
  if (preset?.matrix) {
    const m = preset.matrix();
    const t = (look.intensity ?? 100) / 100;
    list.push(new filters.ColorMatrix({ matrix: m.map((v, i) => ID[i] + (v - ID[i]) * t) }));
  }
  const a = look.adj || {};
  if (a.brightness) list.push(new filters.Brightness({ brightness: a.brightness / 250 }));
  if (a.contrast) list.push(new filters.Contrast({ contrast: a.contrast / 200 }));
  if (a.saturation) list.push(new filters.Saturation({ saturation: a.saturation / 100 }));
  if (a.vibrance) list.push(new filters.Vibrance({ vibrance: a.vibrance / 100 }));
  if (a.hue) list.push(new filters.HueRotation({ rotation: a.hue / 180 }));
  if (a.gamma) { const g = 1 + a.gamma / 140; list.push(new filters.Gamma({ gamma: [g, g, g] })); }
  if (a.sharpen) {
    const k = a.sharpen / 100;
    list.push(new filters.Convolute({ matrix: [0, -k, 0, -k, 1 + 4 * k, -k, 0, -k, 0] }));
  }
  if (a.blur) list.push(new filters.Blur({ blur: a.blur / 200 }));
  if (a.noise) list.push(new filters.Noise({ noise: a.noise * 2.5 }));
  if (a.pixelate) list.push(new filters.Pixelate({ blocksize: 1 + Math.round(a.pixelate / 3) }));
  if (look.tint?.color && look.tint.amount) {
    list.push(new filters.BlendColor({ color: look.tint.color, mode: 'tint', alpha: look.tint.amount / 100 }));
  }
  return list;
}

export function applyLook(img: FabricImage, look: Look): void {
  img.look = look;
  img.filters = lookFilters(look);
  img.applyFilters();
  img.dirty = true;
}

/** Small previews of every look for an image (data URLs, by key). */
export async function lookPreviews(img: FabricImage, size = 84): Promise<Record<string, string>> {
  const el = ((img as Json)._originalElement || img.getElement()) as CanvasImageSource;
  const { w, h } = natural(img);
  const side = Math.min(w, h);
  const small = document.createElement('canvas');
  small.width = size;
  small.height = size;
  small.getContext('2d')!.drawImage(el, (w - side) / 2, (h - side) / 2, side, side, 0, 0, size, size);
  const out: Record<string, string> = {};
  for (const l of LOOKS) {
    const f = new FabricImage(small);
    f.filters = lookFilters({ ...emptyLook(), preset: l.key });
    f.applyFilters();
    out[l.key] = f.toDataURL({ format: 'jpeg', quality: 0.8, multiplier: 1 });
    // let the page breathe between previews
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 0));
  }
  return out;
}

// ---------------------------------------------------------------- shapes, lines and icons

const SHAPE_PATHS: Record<string, string> = {
  arrow: 'M0 30 L60 30 L60 0 L100 50 L60 100 L60 70 L0 70 Z',
  heart: FRAME_SHAPES.heart,
  bubble: 'M12 0 L88 0 C95 0 100 5 100 12 L100 62 C100 69 95 74 88 74 L40 74 L18 100 L22 74 L12 74 C5 74 0 69 0 62 L0 12 C0 5 5 0 12 0 Z',
  hexagon: FRAME_SHAPES.hexagon,
  pentagon: 'M50 0 L100 38 L81 100 L19 100 L0 38 Z',
  diamond: FRAME_SHAPES.diamond,
  badge: starPath(50, 41, 16),
  cross: 'M35 0 L65 0 L65 35 L100 35 L100 65 L65 65 L65 100 L35 100 L35 65 L0 65 L0 35 L35 35 Z',
  parallelogram: 'M25 0 L100 0 L75 100 L0 100 Z',
  arch: FRAME_SHAPES.arch,
  blob: FRAME_SHAPES.blob,
  chevron: 'M0 0 L70 0 L100 50 L70 100 L0 100 L30 50 Z',
};

export const SHAPES: ReadonlyArray<readonly [string, string]> = [
  ['rect', 'Square'], ['rounded', 'Rounded'], ['pill', 'Button'], ['circle', 'Circle'], ['triangle', 'Triangle'], ['star', 'Star'],
  ['arrow', 'Arrow'], ['heart', 'Heart'], ['bubble', 'Speech bubble'], ['badge', 'Badge'], ['hexagon', 'Hexagon'],
  ['pentagon', 'Pentagon'], ['diamond', 'Diamond'], ['cross', 'Plus'], ['parallelogram', 'Slant'], ['chevron', 'Chevron'],
  ['arch', 'Arch'], ['blob', 'Blob'],
];

export function makeShape(kind: string, width: number, height: number, fill = '#0b7a5a'): FabricObject | null {
  const s = Math.round(Math.min(width, height) * 0.3);
  const base = { id: newId(), left: (width - s) / 2, top: (height - s) / 2, fill, strokeWidth: 0, shapeKind: kind, strokeUniform: true };
  switch (kind) {
    case 'rect': return new Rect({ ...base, name: 'Rectangle', width: s, height: s });
    case 'rounded': return new Rect({ ...base, name: 'Rounded box', width: s * 1.4, height: s * 0.7, rx: s * 0.12, ry: s * 0.12 });
    case 'pill': return new Rect({ ...base, name: 'Button', width: s * 1.6, height: s * 0.42, rx: s * 0.21, ry: s * 0.21 });
    case 'circle': return new Circle({ ...base, name: 'Circle', radius: s / 2 });
    case 'triangle': return new Triangle({ ...base, name: 'Triangle', width: s, height: s });
    case 'star': return new Polygon(starPoints(s / 2, s / 4.6, 5), { ...base, name: 'Star' });
    case 'line': return makeLine('solid', width, height);
    default: {
      const d = SHAPE_PATHS[kind];
      if (!d) return null;
      const p = new Path(d, { ...base, name: SHAPES.find((x) => x[0] === kind)?.[1] || 'Shape' });
      p.set({ scaleX: s / p.width, scaleY: (kind === 'bubble' ? s * 0.8 : s) / p.height });
      return p;
    }
  }
}

function starPoints(outer: number, inner: number, n: number): Array<{ x: number; y: number }> {
  const pts: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI / n) * i - Math.PI / 2;
    pts.push({ x: outer + r * Math.cos(a), y: outer + r * Math.sin(a) });
  }
  return pts;
}

export type LineStyle = 'solid' | 'dashed' | 'dotted' | 'arrow' | 'double';

export const LINES: ReadonlyArray<readonly [LineStyle, string]> = [
  ['solid', 'Line'], ['dashed', 'Dashed line'], ['dotted', 'Dotted line'], ['arrow', 'Arrow line'], ['double', 'Two-way arrow'],
];

/** Lines are stroked paths so they keep their thickness when stretched. */
export function makeLine(style: LineStyle, width: number, height: number, color = '#10231e'): Path {
  const len = Math.round(Math.min(width, height) * 0.45);
  const sw = Math.max(4, Math.round(len / 60));
  const head = sw * 4;
  let d = `M0 0 L${len} 0`;
  if (style === 'arrow' || style === 'double') d += ` M${len - head} ${-head} L${len} 0 L${len - head} ${head}`;
  if (style === 'double') d += ` M${head} ${-head} L0 0 L${head} ${head}`;
  return new Path(d, {
    id: newId(), name: LINES.find((l) => l[0] === style)?.[1] || 'Line', shapeKind: `line-${style}`,
    left: (width - len) / 2, top: height / 2 - head, fill: '', stroke: color, strokeWidth: sw, strokeUniform: true,
    strokeLineCap: 'round', strokeLineJoin: 'round',
    strokeDashArray: style === 'dashed' ? [sw * 3, sw * 2] : style === 'dotted' ? [0.1, sw * 2] : null,
  });
}

export const isLine = (o: FabricObject | null | undefined): boolean => typeof o?.shapeKind === 'string' && o.shapeKind.startsWith('line-');

export type IconLibrary = Record<string, IconNode>;

let lucide: Promise<IconLibrary> | null = null;
/** The icon library (loaded on first use; about 1,900 icons). */
export async function iconLibrary(): Promise<IconLibrary> {
  if (!lucide) lucide = import('lucide').then((m) => m.icons as unknown as IconLibrary);
  return lucide;
}

const attrs = (a: Record<string, unknown>): string =>
  Object.entries(a).map(([k, v]) => `${k}="${String(v).replace(/"/g, '&quot;')}"`).join(' ');

/** SVG markup of a library icon. */
export function iconSvg(node: IconNode, color = '#10231e', strokeWidth = 2): string {
  const inner = node.map(([tag, a]) => `<${tag} ${attrs(a)}/>`).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
}

async function svgToGroup(svg: string): Promise<FabricObject> {
  const { objects, options } = await loadSVGFromString(svg);
  return util.groupSVGElements(objects.filter((o): o is FabricObject => !!o), options);
}

export async function makeIcon(name: string, node: IconNode, width: number, height: number, color = '#10231e'): Promise<FabricObject> {
  const g = await svgToGroup(iconSvg(node, color));
  const size = Math.min(width, height) * 0.22;
  const k = size / Math.max(g.width, g.height);
  g.set({
    id: newId(), name: `Icon: ${name}`, icon: { name, color, strokeWidth: 2 }, scaleX: k, scaleY: k,
    left: (width - g.width * k) / 2, top: (height - g.height * k) / 2,
  });
  setIconColor(g, color);
  return g;
}

/** Recolours an icon (all its strokes). */
export function setIconColor(g: FabricObject, color: string): void {
  const each = (o: Json): void => {
    if (o.stroke) o.set('stroke', color);
    if (o.fill && o.fill !== 'none' && o.fill !== '') o.set('fill', color);
    (o._objects || []).forEach(each);
  };
  ((g as Json)._objects || [g]).forEach(each);
  g.icon = { name: '', strokeWidth: 2, ...(g.icon || {}), color };
  g.dirty = true;
}

/** A QR code (vector) that opens a link or starts a WhatsApp chat. */
export async function makeQr(text: string, width: number, height: number, dark = '#10231e', light = '#ffffff'): Promise<FabricObject> {
  const QR = (await import('qrcode')).default;
  const svg = await QR.toString(text, { type: 'svg', margin: 1, errorCorrectionLevel: 'M', color: { dark, light } });
  const g = await svgToGroup(svg);
  const size = Math.min(width, height) * 0.32;
  const k = size / Math.max(g.width, g.height);
  g.set({
    id: newId(), name: 'QR code', qr: { text, dark, light }, scaleX: k, scaleY: k,
    left: (width - g.width * k) / 2, top: (height - g.height * k) / 2,
  });
  return g;
}

// ---------------------------------------------------------------- text

export interface TextPreset {
  key?: string;
  label?: string;
  size: number;
  weight: number;
  family: string;
  fill?: string;
  charSpacing?: number;
}

export const TEXT_PRESETS: Array<TextPreset & { key: string; label: string }> = [
  { key: 'heading', label: 'Add a heading', size: 0.085, weight: 700, family: 'Poppins' },
  { key: 'subheading', label: 'Add a subheading', size: 0.05, weight: 600, family: 'Poppins' },
  { key: 'body', label: 'Add body text', size: 0.032, weight: 400, family: 'Poppins' },
  { key: 'display', label: 'BIG SALE', size: 0.13, weight: 400, family: 'Bebas Neue' },
  { key: 'script', label: 'Thank you', size: 0.09, weight: 700, family: 'Dancing Script' },
  { key: 'hindi', label: 'शुभ दीपावली', size: 0.07, weight: 700, family: 'Noto Sans Devanagari' },
  { key: 'serif', label: 'Grand Opening', size: 0.08, weight: 900, family: 'Playfair Display' },
  { key: 'marker', label: 'Limited stock!', size: 0.07, weight: 400, family: 'Permanent Marker' },
  { key: 'anton', label: 'FLAT 50% OFF', size: 0.1, weight: 400, family: 'Anton' },
  { key: 'pacifico', label: 'Fresh & tasty', size: 0.075, weight: 400, family: 'Pacifico' },
];

export interface ComboPart { text: string; family: string; weight: number; size: number; charSpacing?: number }
export interface TextCombo { key: string; top: ComboPart; bottom: ComboPart }

/** Two-line combinations, added as two texts selected together. */
export const TEXT_COMBOS: TextCombo[] = [
  { key: 'sale', top: { text: 'MEGA SALE', family: 'Anton', weight: 400, size: 0.14 }, bottom: { text: 'Up to 50% off · this weekend only', family: 'Poppins', weight: 500, size: 0.035 } },
  { key: 'open', top: { text: 'Now open', family: 'Pacifico', weight: 400, size: 0.1 }, bottom: { text: 'VISIT US TODAY', family: 'Montserrat', weight: 800, size: 0.04, charSpacing: 300 } },
  { key: 'diwali', top: { text: 'Happy Diwali', family: 'Great Vibes', weight: 400, size: 0.12 }, bottom: { text: 'शुभ दीपावली', family: 'Yatra One', weight: 400, size: 0.05 } },
  { key: 'thanks', top: { text: 'THANK YOU', family: 'Playfair Display', weight: 900, size: 0.1 }, bottom: { text: 'for shopping with us', family: 'Lora', weight: 400, size: 0.04 } },
];

export function makeText(preset: TextPreset, width: number, height: number, text?: string): Textbox {
  const fontSize = Math.round(Math.min(width, height) * preset.size * (width / Math.min(width, height) > 1.6 ? 1.2 : 1));
  const w = Math.round(width * 0.8);
  return new Textbox(text ?? preset.label ?? '', {
    id: newId(), name: 'Text', left: (width - w) / 2, top: height / 2 - fontSize / 2, width: w, fontSize,
    fontFamily: preset.family, fontWeight: preset.weight, fill: preset.fill || '#10231e', textAlign: 'center', lineHeight: 1.16,
    charSpacing: preset.charSpacing || 0,
  });
}

export function makeCombo(combo: TextCombo, width: number, height: number): [Textbox, Textbox] {
  const a = makeText({ ...combo.top, label: combo.top.text }, width, height);
  const b = makeText({ ...combo.bottom, label: combo.bottom.text }, width, height);
  const total = a.height + b.height * 1.3;
  a.set({ top: (height - total) / 2 });
  b.set({ top: (a.top as number) + a.height + b.height * 0.3 });
  return [a, b];
}

export const TEXT_EFFECTS: ReadonlyArray<readonly [TextEffectType, string]> = [
  ['none', 'None'], ['shadow', 'Shadow'], ['lift', 'Lift'], ['hollow', 'Hollow'], ['splice', 'Splice'],
  ['outline', 'Outline'], ['neon', 'Neon'], ['glitch', 'Glitch'], ['background', 'Background'],
];

/** Canva-style text effects. effect = { type, color, amount (0–100) }. */
export function applyTextEffect(t: Textbox, effect: TextEffect): void {
  const prev = t.effect;
  const base = effect.baseFill ?? prev?.baseFill ?? (typeof t.fill === 'string' && t.fill !== 'transparent' ? t.fill : '#10231e');
  const fs = t.fontSize;
  const k = (effect.amount ?? 50) / 50;
  const color = effect.color;
  t.set({ fill: base, stroke: null, strokeWidth: 0, shadow: null, textBackgroundColor: '', paintFirst: 'fill' });
  switch (effect.type) {
    case 'shadow':
      t.set({ shadow: { color: hexAlpha(color || '#000000', 0.45), blur: fs * 0.08 * k, offsetX: fs * 0.05 * k, offsetY: fs * 0.05 * k } });
      break;
    case 'lift':
      t.set({ shadow: { color: `rgba(0,0,0,${0.18 + 0.2 * k})`, blur: fs * 0.3 * k, offsetX: 0, offsetY: fs * 0.08 * k } });
      break;
    case 'hollow':
      t.set({ fill: 'transparent', stroke: base, strokeWidth: Math.max(1, fs * 0.03 * k) });
      break;
    case 'splice':
      t.set({
        fill: 'transparent', stroke: base, strokeWidth: Math.max(1, fs * 0.025),
        shadow: { color: color || '#ffd166', blur: 0, offsetX: fs * 0.06 * k, offsetY: fs * 0.06 * k },
      });
      break;
    case 'outline':
      t.set({ stroke: color || '#ffffff', strokeWidth: Math.max(1, fs * 0.07 * k), paintFirst: 'stroke', strokeLineJoin: 'round' });
      break;
    case 'neon':
      t.set({ fill: lighten(base, 0.55), shadow: { color: base, blur: fs * 0.35 * k, offsetX: 0, offsetY: 0 } });
      break;
    case 'glitch':
      t.set({
        shadow: { color: color || '#00e5ff', blur: 0, offsetX: -fs * 0.04 * k, offsetY: 0 },
        stroke: '#ff2d6f', strokeWidth: Math.max(1, fs * 0.012), paintFirst: 'stroke',
      });
      break;
    case 'background':
      t.set({ textBackgroundColor: color || '#ffd166' });
      break;
    default:
      break;
  }
  t.effect = effect.type === 'none' ? null : { ...effect, baseFill: base };
  t.dirty = true;
}

/** Text colour as the user sees it (hollow text keeps its colour in the effect). */
export const textColor = (t: FabricObject): string => t.effect?.baseFill || (typeof t.fill === 'string' ? t.fill : '#10231e');

export function setTextColor(t: Textbox, color: string): void {
  if (t.effect) applyTextEffect(t, { ...t.effect, baseFill: color });
  else t.set({ fill: color });
}

function hexAlpha(hex: string, a: number): string {
  const h = toHex6(hex);
  return `rgba(${parseInt(h.slice(1, 3), 16)},${parseInt(h.slice(3, 5), 16)},${parseInt(h.slice(5, 7), 16)},${a})`;
}

function lighten(hex: string, amount: number): string {
  const h = toHex6(hex);
  const ch = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)).map((v) => Math.round(v + (255 - v) * amount));
  return `#${ch.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

export function toHex6(c: unknown): string {
  if (!c || typeof c !== 'string') return '#000000';
  if (/^#[0-9a-f]{6}$/i.test(c)) return c.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(c)) return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`.toLowerCase();
  const m = c.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (m) return `#${[m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, '0')).join('')}`;
  return '#000000';
}

/** Every solid colour used on a page (for the "Document colours" row). */
export function documentColors(canvas: AnyCanvas): string[] {
  const out = new Set<string>();
  const add = (v: unknown): void => { if (typeof v === 'string' && v && v !== 'transparent' && !v.startsWith('url')) out.add(toHex6(v)); };
  const walkObj = (o: FabricObject): void => {
    add(o.fill); add(o.stroke); if (o.effect?.baseFill) add(o.effect.baseFill);
    ((o as Json)._objects || []).forEach(walkObj);
  };
  canvas.getObjects().forEach(walkObj);
  add(canvas.backgroundColor);
  return [...out].slice(0, 16);
}

// ---------------------------------------------------------------- save and load

/** The page as JSON text, as it is in this session (images point at local object URLs). */
export const snapshot = (canvas: AnyCanvas): string => JSON.stringify(canvas.toObject(EXTRA_PROPS));

/** Images point at their stored file, not the session's object URL. */
function storable(json: Json): Json {
  walk(json.objects, (o) => {
    if (o.assetUrl && o.type?.toLowerCase() === 'image') o.src = o.assetUrl;
  });
  if (json.backgroundImage?.assetUrl) json.backgroundImage.src = json.backgroundImage.assetUrl;
  return json;
}

/** Canvas → JSON string (a single page). */
export function serialize(canvas: AnyCanvas): string {
  return JSON.stringify(storable(canvas.toObject(EXTRA_PROPS)));
}

/** Loads a page, fetching stored images through the proxy first. null = an empty white page. */
export async function deserialize(canvas: AnyCanvas, jsonString: string | object | null | undefined): Promise<void> {
  if (!jsonString) {
    canvas.clear();
    canvas.backgroundColor = '#ffffff';
    canvas.requestRenderAll();
    return;
  }
  const json: Json = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
  const jobs: Array<Promise<void>> = [];
  walk(json.objects, (o) => {
    if (o.assetUrl && o.type?.toLowerCase() === 'image') {
      jobs.push(assetObjectUrl(o.assetUrl).then((u) => { o.src = u; }).catch(() => { o.src = BROKEN; }));
    }
  });
  await Promise.all(jobs);
  await canvas.loadFromJSON(json);
  canvas.getObjects().forEach((o) => { if (!o.id) o.id = newId(); });
  canvas.requestRenderAll();
}

function walk(list: Json[] | undefined, fn: (o: Json) => void): void {
  for (const o of list || []) {
    fn(o);
    if (o.objects) walk(o.objects, fn);
  }
}

const BROKEN = `data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="#dfe7e3"/><text x="200" y="160" font-family="sans-serif" font-size="22" text-anchor="middle" fill="#4f625b">Image not found</text></svg>')}`;

// ---------------------------------------------------------------- pages

export interface PageEntry { id: string; json: string | null }

/**
 * A design's saved canvasJson holds every page: {version: 2, pages: [{id, json}]}.
 * Designs saved by the first Studio version (one Fabric page) still open as a one-page design.
 */
export function parseDoc(canvasJson: string | object | null | undefined): PageEntry[] {
  if (!canvasJson) return [{ id: newId(), json: null }];
  const parsed: Json = typeof canvasJson === 'string' ? JSON.parse(canvasJson) : canvasJson;
  if (parsed && parsed.version === 2 && Array.isArray(parsed.pages)) {
    return parsed.pages.map((p: Json) => ({ id: p.id || newId(), json: p.json ? JSON.stringify(p.json) : null }));
  }
  return [{ id: newId(), json: JSON.stringify(parsed) }];
}

/** Pages → the canvasJson saved on the server. */
export function stringifyDoc(pages: PageEntry[]): string {
  return JSON.stringify({
    version: 2,
    pages: pages.map((p) => ({ id: p.id, json: p.json ? storable(JSON.parse(p.json)) : null })),
  });
}

/** An off-screen canvas with a page loaded. Call dispose() when done. */
export async function offscreen(json: string | null, width: number, height: number): Promise<StaticCanvas> {
  const el = document.createElement('canvas');
  const c = new StaticCanvas(el, { width, height, enableRetinaScaling: false, renderOnAddRemove: false });
  await deserialize(c, json);
  c.renderAll();
  return c;
}

export interface RenderOptions { format?: 'png' | 'jpeg'; multiplier?: number; quality?: number; transparent?: boolean }

/** A page as an image. format png | jpeg; transparent drops the background colour. */
export async function renderPage(
  json: string | null, width: number, height: number,
  { format = 'png', multiplier = 1, quality = 0.92, transparent = false }: RenderOptions = {},
): Promise<string> {
  const c = await offscreen(json, width, height);
  if (transparent) {
    c.backgroundColor = "";
    c.renderAll();
  }
  const url = c.toDataURL({ format, quality, multiplier });
  c.dispose();
  return url;
}

export const renderPageThumb = (json: string | null, width: number, height: number, maxSide = 200): Promise<string> =>
  renderPage(json, width, height, { format: 'jpeg', quality: 0.75, multiplier: maxSide / Math.max(width, height) });

/** Renders a template layout to a small PNG data URL (cards on the Studio home). */
export async function renderSpecPreview(spec: TemplateSpec, width: number, height: number, maxSide = 360): Promise<string> {
  const el = document.createElement('canvas');
  const c = new StaticCanvas(el, { width, height, enableRetinaScaling: false, renderOnAddRemove: false });
  buildFromSpec(c, spec, width, height);
  c.renderAll();
  const url = c.toDataURL({ format: 'png', multiplier: maxSide / Math.max(width, height) });
  c.dispose();
  return url;
}

// ---------------------------------------------------------------- magic resize

/** Scales one object evenly by k and moves its centre to (x, y). Text keeps crisp by changing its font size. */
function placeScaled(o: FabricObject, k: number, x: number, y: number): void {
  const t = (o.type || '').toLowerCase();
  if (t === 'textbox') {
    const tb = o as Textbox;
    tb.set({ fontSize: tb.fontSize * k, width: tb.width * k });
    if (tb.strokeWidth) tb.set({ strokeWidth: tb.strokeWidth * k });
    if (tb.shadow) {
      const sh = tb.shadow as { blur: number; offsetX: number; offsetY: number; color: string };
      tb.set({ shadow: { ...sh, blur: sh.blur * k, offsetX: sh.offsetX * k, offsetY: sh.offsetY * k } });
    }
    tb.initDimensions();
  } else {
    o.set({ scaleX: o.scaleX * k, scaleY: o.scaleY * k });
  }
  o.setPositionByOrigin(new Point(x, y), 'center', 'center');
  o.setCoords();
}

/** Stretches a box-like object (rectangle or unframed photo) to exactly cover a rectangle. */
function coverRect(o: FabricObject, r: Box): void {
  if ((o.type || '').toLowerCase() === 'image') {
    fitImageToFrame(o as FabricImage, { x: r.left, y: r.top, w: r.width, h: r.height, radius: 0 });
    o.set({ frame: undefined });
  } else {
    const b = o.getBoundingRect();
    o.set({ scaleX: o.scaleX * (r.width / b.width), scaleY: o.scaleY * (r.height / b.height) });
    o.setPositionByOrigin(new Point(r.left + r.width / 2, r.top + r.height / 2), 'center', 'center');
    o.setCoords();
  }
}

const union = (rects: Box[]): Box => {
  const l = Math.min(...rects.map((r) => r.left));
  const t = Math.min(...rects.map((r) => r.top));
  return { left: l, top: t, width: Math.max(...rects.map((r) => r.left + r.width)) - l, height: Math.max(...rects.map((r) => r.top + r.height)) - t };
};

interface ObjInfo { b: Box; center: Point; wide: boolean; tall: boolean; stretchable: boolean }

/**
 * Re-lays a page out for a new size (magic resize).
 * - Similar shape: the layout is scaled evenly and centred; anything spanning the page (background bars, photos,
 *   gradient) is extended to fill it, so nothing is stretched, overlaps or leaves empty bands.
 * - Much taller (e.g. banner → Story) or much wider (Story → banner): a two-column layout is stacked (or a stacked
 *   layout put side by side), each half scaled to fill its part of the new page, with its background panel following it.
 */
export async function resizePageJson(json: string | null, w1: number, h1: number, w2: number, h2: number): Promise<string | null> {
  if (!json) return null;
  const c = await offscreen(json, w1, h1);
  const sx = w2 / w1;
  const sy = h2 / h1;
  const s = Math.min(sx, sy);
  const change = (w2 / h2) / (w1 / h1);
  const all = c.getObjects();
  const info = new Map<FabricObject, ObjInfo>(all.map((o) => {
    const b = o.getBoundingRect();
    const t = (o.type || '').toLowerCase();
    const stretchable = t === 'rect' || (t === 'image' && !o.frame);
    return [o, { b, center: o.getCenterPoint(), wide: b.width >= w1 * 0.9 && !o.angle, tall: b.height >= h1 * 0.9 && !o.angle, stretchable }];
  }));
  const I = (o: FabricObject): ObjInfo => info.get(o)!;

  for (const o of all.filter((x) => x.isBackgroundImage)) coverRect(o, { left: 0, top: 0, width: w2, height: h2 });
  const rest = all.filter((x) => !x.isBackgroundImage);

  let done = false;
  if (change < 0.6 || change > 1.66) {
    // stack (taller) or put side by side (wider)
    const vertical = change < 0.6;
    const along: 'x' | 'y' = vertical ? 'x' : 'y';
    const half = vertical ? w1 / 2 : h1 / 2;
    const isPanel = (o: FabricObject): boolean => { const i = I(o); return i.stretchable && (vertical ? i.tall && !i.wide : i.wide && !i.tall); };
    const isFull = (o: FabricObject): boolean => { const i = I(o); return i.stretchable && i.wide && i.tall; };
    const content = rest.filter((o) => !isPanel(o) && !isFull(o));
    const A = content.filter((o) => I(o).center[along] < half);
    const B = content.filter((o) => I(o).center[along] >= half);
    if (A.length && B.length) {
      const bA = union(A.map((o) => I(o).b));
      const bB = union(B.map((o) => I(o).b));
      const mainA = vertical ? bA.height : bA.width;
      const mainB = vertical ? bB.height : bB.width;
      const fracA = Math.min(0.7, Math.max(0.3, mainA / (mainA + mainB)));
      const regions: Box[] = vertical
        ? [{ left: 0, top: 0, width: w2, height: h2 * fracA }, { left: 0, top: h2 * fracA, width: w2, height: h2 * (1 - fracA) }]
        : [{ left: 0, top: 0, width: w2 * fracA, height: h2 }, { left: w2 * fracA, top: 0, width: w2 * (1 - fracA), height: h2 }];
      const groups: Array<[FabricObject[], Box, Box]> = [[A, bA, regions[0]], [B, bB, regions[1]]];
      groups.forEach(([list, bb, r]) => {
        const k = Math.min((r.width * 0.86) / bb.width, (r.height * 0.86) / bb.height);
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        list.forEach((o) => {
          const i = I(o);
          placeScaled(o, k, cx + (i.center.x - (bb.left + bb.width / 2)) * k, cy + (i.center.y - (bb.top + bb.height / 2)) * k);
        });
      });
      rest.filter(isPanel).forEach((o) => coverRect(o, I(o).center[along] < half ? regions[0] : regions[1]));
      rest.filter(isFull).forEach((o) => coverRect(o, { left: 0, top: 0, width: w2, height: h2 }));
      done = true;
    }
  }

  if (!done) {
    const ox = (w2 - w1 * s) / 2;
    const oy = (h2 - h1 * s) / 2;
    for (const o of rest) {
      const { b, center, wide, tall, stretchable } = I(o);
      if (stretchable && (wide || tall)) {
        coverRect(o, {
          left: wide ? (b.left / w1) * w2 : ox + b.left * s, top: tall ? (b.top / h1) * h2 : oy + b.top * s,
          width: wide ? (b.width / w1) * w2 : b.width * s, height: tall ? (b.height / h1) * h2 : b.height * s,
        });
      } else {
        placeScaled(o, s, ox + center.x * s, oy + center.y * s);
      }
    }
  }

  for (const o of rest) {
    if (o.frame) {
      const nb = o.getBoundingRect();
      o.frame = { ...o.frame, x: nb.left, y: nb.top, w: nb.width, h: nb.height, radius: (o.frame.radius || 0) * (nb.width / I(o).b.width) };
    }
  }
  const bg = c.backgroundColor as Json;
  if (bg && typeof bg === 'object' && bg.coords) {
    const k = bg.coords;
    bg.coords = { ...k, x1: k.x1 * sx, x2: k.x2 * sx, y1: k.y1 * sy, y2: k.y2 * sy, r1: k.r1 != null ? k.r1 * s : k.r1, r2: k.r2 != null ? k.r2 * s : k.r2 };
  }
  const out = JSON.stringify(c.toObject(EXTRA_PROPS));
  c.dispose();
  return out;
}

// ---------------------------------------------------------------- alignment

export type AlignWhere = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom';

/** Aligns objects to the page (one object) or to each other (several). */
export function alignObjects(objects: FabricObject[], where: AlignWhere, pageW: number, pageH: number): void {
  const rects = objects.map((o) => o.getBoundingRect());
  const area = objects.length === 1
    ? { left: 0, top: 0, right: pageW, bottom: pageH }
    : {
      left: Math.min(...rects.map((r) => r.left)), top: Math.min(...rects.map((r) => r.top)),
      right: Math.max(...rects.map((r) => r.left + r.width)), bottom: Math.max(...rects.map((r) => r.top + r.height)),
    };
  objects.forEach((o, i) => {
    if (o.locked) return;
    const r = rects[i];
    let dx = 0;
    let dy = 0;
    if (where === 'left') dx = area.left - r.left;
    if (where === 'center') dx = (area.left + area.right) / 2 - (r.left + r.width / 2);
    if (where === 'right') dx = area.right - (r.left + r.width);
    if (where === 'top') dy = area.top - r.top;
    if (where === 'middle') dy = (area.top + area.bottom) / 2 - (r.top + r.height / 2);
    if (where === 'bottom') dy = area.bottom - (r.top + r.height);
    o.set({ left: o.left + dx, top: o.top + dy });
    o.setCoords();
  });
}

/** Even spacing between 3+ objects. axis: h | v. */
export function distributeObjects(objects: FabricObject[], axis: 'h' | 'v'): void {
  if (objects.length < 3) return;
  const items = objects.map((o) => ({ o, r: o.getBoundingRect() }));
  const pos = axis === 'h' ? 'left' : 'top';
  const size = axis === 'h' ? 'width' : 'height';
  items.sort((a, b) => a.r[pos] - b.r[pos]);
  const first = items[0].r[pos];
  const last = items[items.length - 1].r[pos] + items[items.length - 1].r[size];
  const used = items.reduce((sum, it) => sum + it.r[size], 0);
  const gap = (last - first - used) / (items.length - 1);
  let at = first;
  for (const it of items) {
    const d = at - it.r[pos];
    it.o.set(pos, it.o[pos] + d);
    it.o.setCoords();
    at += it.r[size] + gap;
  }
}

// ---------------------------------------------------------------- smart guides

type Guide = { x: number; y?: undefined } | { y: number; x?: undefined };

/**
 * Snaps a moving object to the page edges and centre and to other objects' edges and centres,
 * and draws pink guide lines while it is snapped (like Canva). Returns a cleanup function.
 */
export function attachSnapping(
  canvas: Canvas,
  pageSize: () => { width: number; height: number },
  isOn: () => boolean = () => true,
): () => void {
  let guides: Guide[] = [];
  const onMove = (e: { target?: FabricObject }): void => {
    guides = [];
    const o = e.target;
    if (!o || !isOn()) return;
    const { width: W, height: H } = pageSize();
    const zoom = canvas.getZoom();
    const thr = 7 / zoom;
    const inside = o instanceof Group ? o.getObjects() : [];
    const others = canvas.getObjects().filter((x) => x !== o && x.visible !== false && !inside.includes(x) && !x.isBackgroundImage);
    const xs = [0, W / 2, W];
    const ys = [0, H / 2, H];
    for (const x of others) {
      const r = x.getBoundingRect();
      xs.push(r.left, r.left + r.width / 2, r.left + r.width);
      ys.push(r.top, r.top + r.height / 2, r.top + r.height);
    }
    const r = o.getBoundingRect();
    const best = (mine: number[], lines: number[]): { d: number; at: number } | null => {
      let hit: { d: number; at: number } | null = null;
      for (const m of mine) {
        for (const l of lines) {
          const d = l - m;
          if (Math.abs(d) < thr && (!hit || Math.abs(d) < Math.abs(hit.d))) hit = { d, at: l };
        }
      }
      return hit;
    };
    const hx = best([r.left, r.left + r.width / 2, r.left + r.width], xs);
    const hy = best([r.top, r.top + r.height / 2, r.top + r.height], ys);
    if (hx) { o.set('left', o.left + hx.d); guides.push({ x: hx.at }); }
    if (hy) { o.set('top', o.top + hy.d); guides.push({ y: hy.at }); }
    if (hx || hy) o.setCoords();
  };
  const clear = (): void => { if (guides.length) { guides = []; canvas.requestRenderAll(); } };
  const draw = ({ ctx }: { ctx: CanvasRenderingContext2D }): void => {
    if (!guides.length) return;
    const { width: W, height: H } = pageSize();
    const v = canvas.viewportTransform;
    ctx.save();
    ctx.transform(v[0], v[1], v[2], v[3], v[4], v[5]);
    ctx.strokeStyle = '#e5368f';
    ctx.lineWidth = 1.5 / canvas.getZoom();
    ctx.setLineDash([6 / canvas.getZoom(), 4 / canvas.getZoom()]);
    for (const g of guides) {
      ctx.beginPath();
      if (g.x != null) { ctx.moveTo(g.x, 0); ctx.lineTo(g.x, H); } else { ctx.moveTo(0, g.y as number); ctx.lineTo(W, g.y as number); }
      ctx.stroke();
    }
    ctx.restore();
  };
  canvas.on('object:moving', onMove);
  canvas.on('mouse:up', clear);
  canvas.on('after:render', draw);
  return () => {
    canvas.off('object:moving', onMove);
    canvas.off('mouse:up', clear);
    canvas.off('after:render', draw);
  };
}

export { Canvas };